import { z } from 'zod';
import { Dateish, intList, PaginationQuerySchema } from './common';
import { EquipmentSgrSchema } from './sgr-vehicles';

const ISODate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

// ── Tipos de recurso ────────────────────────────────────────────────────────
export const EquipmentTypeSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  active: z.boolean(),
  warrantyNotifyEmail: z.string().nullable(),
  warrantyNotifyDaysBefore: z.number().int().nullable(),
});
export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;

export const EquipmentTypeBodySchema = z.object({
  name: z.string().min(1),
  warrantyNotifyEmail: z.string().email().nullish(),
  warrantyNotifyDaysBefore: z.number().int().min(1).max(365).nullish(),
});
export type EquipmentTypeBody = z.infer<typeof EquipmentTypeBodySchema>;

// ── Grupos de recursos (por cliente) ────────────────────────────────────────
export const EquipmentGroupSchema = z.object({
  id: z.number().int(),
  clientId: z.number().int(),
  clientName: z.string(),
  name: z.string(),
  equipmentCount: z.number().int(),
});
export type EquipmentGroup = z.infer<typeof EquipmentGroupSchema>;

export const EquipmentGroupBodySchema = z.object({
  clientId: z.number().int().positive(),
  name: z.string().min(1),
});
export type EquipmentGroupBody = z.infer<typeof EquipmentGroupBodySchema>;

// ── Recursos ────────────────────────────────────────────────────────────────
export const EquipmentSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /**
   * O cliente do recurso. null só no veículo do SGR (spec 032) cujo ponto de
   * venda ainda não está ligado a um cliente: ponto sem cliente não adivinha.
   */
  clientId: z.number().int().nullable(),
  clientName: z.string().nullable(),
  equipmentTypeId: z.number().int(),
  equipmentTypeName: z.string(),
  equipmentGroupId: z.number().int().nullable(),
  equipmentGroupName: z.string().nullable(),
  serialNumber: z.string().nullable(),
  imei: z.string().nullable(),
  acquisitionDate: ISODate.nullable(),
  warrantyDate: ISODate.nullable(),
  /** garantia vence em <= 30 dias (ou já venceu) */
  warrantyExpiring: z.boolean(),
  active: z.boolean(),
  notes: z.string().nullable(),
  createdAt: Dateish,
  /** `cod_veiculo` quando o recurso é um veículo do SGR (spec 032); null = cadastrado à mão */
  sgrId: z.string().nullable(),
  /** o associado do veículo do SGR (a coluna "Associado" da lista) */
  endCustomer: z.object({ id: z.number().int(), name: z.string() }).nullable(),
});
export type Equipment = z.infer<typeof EquipmentSchema>;

export const EquipmentDetailSchema = EquipmentSchema.extend({
  openTicketsCount: z.number().int(),
  /** o bloco do SGR (spec 032, §7) — só leitura no ERP: o SGR vence sempre ali */
  sgr: EquipmentSgrSchema.nullable(),
});
export type EquipmentDetail = z.infer<typeof EquipmentDetailSchema>;

export const EquipmentBodySchema = z.object({
  name: z.string().min(1),
  /**
   * Obrigatório para o recurso cadastrado à mão. null só passa no PUT de um
   * veículo do SGR que ainda está sem cliente — e lá o cliente não muda pela
   * tela (409 resource_from_sgr): quem decide é o ponto de venda.
   */
  clientId: z.number().int().positive().nullable(),
  equipmentTypeId: z.number().int().positive(),
  equipmentGroupId: z.number().int().positive().nullish(),
  serialNumber: z.string().nullish(),
  imei: z.string().nullish(),
  acquisitionDate: ISODate.nullish(),
  warrantyDate: ISODate.nullish(),
  active: z.boolean().default(true),
  notes: z.string().nullish(),
});
export type EquipmentBody = z.infer<typeof EquipmentBodySchema>;

export const EquipmentListQuerySchema = PaginationQuerySchema.extend({
  client_ids: intList.optional(),
  type_ids: intList.optional(),
  group_id: z.coerce.number().int().positive().optional(),
  active: z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1').optional(),
  /** ILIKE em nome, serial e IMEI */
  search: z.string().optional(),
});
export type EquipmentListQuery = z.infer<typeof EquipmentListQuerySchema>;
